import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { useInheritClients, useSaveProject, useFolders } from "@/hooks/useData";
import { useClients } from "@/hooks/useTime";
import { descendantIds } from "@/lib/deps";
import { money } from "@/lib/timeRollup";
import { projectRate } from "@/lib/billing";
import { PROJECT_PALETTE, PROJECT_STATUSES, PROJECT_STATUS_LABEL, type BillingType, type Project, type ProjectCategory, type ProjectStatus } from "@/lib/types";
import { useToast } from "@/context/ToastContext";
import { statusEditable } from "@/lib/projectStatusSync";



// Todoist has no workspaces below the Business plan, so this split is ours.
const CATEGORIES: [ProjectCategory, string][] = [["work", "Work"], ["personal", "Personal"]];

// Which burn-down the reports draw. Fixed-fee spends once over the engagement;
// a retainer resets every month.
const BILLING: [BillingType, string][] = [
  ["hourly", "Hourly"], ["fixed", "Fixed fee"], ["retainer", "Monthly retainer"], ["internal", "Internal (your own work)"],
  ["none", "Not set (counts as hourly)"],
];

/** What the billing type means for the time logged against the project. */
const BILLING_HINT: Record<BillingType, string> = {
  hourly: "Time logs as Billable at the rate, or No charge. Billable time waits on an invoice.",
  none: "Treated as hourly: time logs as Billable at the rate, or No charge.",
  fixed: "Time logs as Project work: no charge per hour, counted against the budgeted hours.",
  retainer: "Time logs as Project work: no charge per hour, counted against the monthly hours.",
  internal: "Your own work, no client: every hour logs as Internal and is never charged.",
};

export function ProjectModal({
  project, allProjects, defaultParent, onClose,
}: {
  project: Project | null;          // null = new
  allProjects: Project[];
  defaultParent?: string;
  onClose: () => void;
}) {
  const save = useSaveProject();
  const inheritClients = useInheritClients();
  const { data: folders = [] } = useFolders();
  const { data: clients = [] } = useClients();
  const toast = useToast();
  const isNew = !project;

  const [name, setName] = useState(project?.name || "");
  // A project can sit in a folder OR under a parent. One that somehow has both
  // (a folder picked on a sub-project's row) is shown — and saved — under its
  // parent, which is how every list already displays it and what Todoist says.
  // Starting from the folder instead used to drop the parent on the next save,
  // whatever was being edited.
  const [folderId, setFolderId] = useState(project?.parent ? "" : (project?.folderId || ""));
  const [client, setClient] = useState(project?.client || "");
  const [description, setDescription] = useState(project?.description || "");
  // Older stages (planning, hold, complete) read as Active and save as it.
  const [status, setStatus] = useState<ProjectStatus>(project?.status === "archived" ? "archived" : "active");
  const editable = statusEditable(project);
  const [color, setColor] = useState(project?.color || PROJECT_PALETTE[0]);
  const [parent, setParent] = useState(project?.parent || defaultParent || "");
  const [due, setDue] = useState(project?.due?.slice(0, 10) || "");
  const [start, setStart] = useState(project?.start?.slice(0, 10) || "");
  const [contract, setContract] = useState(project?.contract || "");
  const [scope, setScope] = useState(project?.scope || "");
  const [category, setCategory] = useState<ProjectCategory>(project?.category || "work");
  const [billingType, setBillingType] = useState<BillingType>(project?.billingType || "none");
  // PocketBase saves a blank number as 0, so 0 shows blank. A project with only
  // the old free-text fee ("$45,000") gets its amount filled in from it.
  const [budgetHours, setBudgetHours] = useState(project?.budgetHours ? String(project.budgetHours) : "");
  const [feeAmount, setFeeAmount] = useState(
    project?.feeAmount ? String(project.feeAmount) : feeFromText(project?.fee),
  );
  const [retainerHours, setRetainerHours] = useState(project?.retainerHours ? String(project.retainerHours) : "");
  /** This project's own hourly rate. Blank uses the client's. */
  // A stored 0 is a blank PocketBase saved as 0: show it blank.
  const [rate, setRate] = useState(projectRate(project)?.toString() ?? "");

  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // A project can't be parented to itself or any of its descendants.
  const forbidden = project ? descendantIds(project.id, allProjects) : new Set<string>();
  const parentOptions = allProjects.filter((p) => p.id !== project?.id && !forbidden.has(p.id));

  async function submit() {
    if (!name.trim()) { toast("A name is required"); return; }
    try {
      await save.mutateAsync({
        id: project?.id,
        name: name.trim(), client, description, status, color,
        // A project sits in a folder OR under a parent, not both. If a folder is
        // chosen, parent is cleared; otherwise folderId is cleared.
        parent: folderId ? null : (parent || null),
        folderId: folderId || null,
        due: due || undefined,
        start: start || undefined,
        contract, scope, category,
        billingType,
        // Empty means "no budget", which must round-trip as null rather than 0 —
        // a 0-hour budget would read as instantly over.
        budgetHours: budgetHours === "" ? null : Number(budgetHours),
        feeAmount: feeAmount === "" ? null : Number(feeAmount),
        retainerHours: retainerHours === "" ? null : Number(retainerHours),
        // Blank round-trips as null so the client's rate applies, not $0.
        defaultRate: rate === "" ? null : Number(rate),
        // Free text stays the display name (the rest of the app reads it), but
        // an exact name match also sets the relation, which is what rates
        // resolve through. A typo just means no link — never a wrong one.
        clientRef: clients.find(
          (c) => c.name.trim().toLowerCase() === client.trim().toLowerCase(),
        )?.id ?? null,
        taskListName: project?.taskListName || name.trim(),
      } as any);
      // Its sub-projects with no client take this one's.
      const subs = await inheritClients();
      toast((isNew ? "Project created" : "Project saved") + (subs ? ` · ${subs} sub-project${subs === 1 ? "" : "s"} linked to the client too` : ""));
      onClose();
    } catch {
      toast("Save failed");
    }
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "60px 20px", overflowY: "auto", animation: "fadein 0.12s ease-out" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 520, maxWidth: "100%", background: "var(--raised)", borderRadius: 14, padding: "26px 28px 24px", animation: "pop 0.15s ease-out", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 500, letterSpacing: "-0.02em" }}>{isNew ? "New project" : "Edit project"}</h2>
          <button onClick={onClose} style={iconBtn} aria-label="Close">✕</button>
        </div>

        {project?.todoistId && (
          <div style={{
            fontSize: 11.5, color: "var(--ink3)", background: "var(--tint)",
            border: "1px solid var(--line)", borderRadius: 8, padding: "9px 11px", marginBottom: 16,
          }}>
            Mirrored from Todoist. <strong>Name, dates and colour are overwritten on the next
            sync</strong> — change those in Todoist. Archiving follows Todoist too: archive or
            unarchive it there. Everything else here (category, client, fee, contract, scope, folder,
            and any other status) is Project Hub's own and is never touched by a sync.
          </div>
        )}

        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} style={input} autoFocus placeholder="e.g. Strategic Plan Refresh" />
        </Field>
        <div>
          <Field label="Client">
            {/* A select, not a text field with suggestions: the link is what
                gives logged time its rate, and a free-text box made it look
                optional and easy to typo. */}
            <div style={{ position: "relative" }}>
              <select value={client} onChange={(e) => setClient(e.target.value)} style={{ ...input, cursor: "pointer" }}>
                <option value="">— No client</option>
                {clients.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {/* A name inherited from before this list existed stays
                    selectable rather than being silently cleared on save. */}
                {client.trim() && !clients.some((c) => c.name.trim().toLowerCase() === client.trim().toLowerCase()) && (
                  <option value={client}>{client} (not in Clients)</option>
                )}
              </select>
            </div>
            {clients.length === 0 ? (
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>
                No clients yet — add one under Clients to set a rate.
              </div>
            ) : client.trim() && !clients.some((c) => c.name.trim().toLowerCase() === client.trim().toLowerCase()) ? (
              <div style={{ fontSize: 11, color: "var(--warn)", marginTop: 5 }}>
                Not a client record, so time logged here gets no rate.
              </div>
            ) : null}
          </Field>
        </div>
        <Row>
          <Field label="Start date"><input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={input} /></Field>
          <Field label="Due date"><input type="date" value={due} onChange={(e) => setDue(e.target.value)} style={input} /></Field>
        </Row>
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...input, minHeight: 66, resize: "vertical", background: "var(--surface2)" }} placeholder="One-line summary of the engagement" />
        </Field>

        <Row>
          <Field label="Category">
            <div style={{ position: "relative" }}>
              <select value={category} onChange={(e) => setCategory(e.target.value as ProjectCategory)} style={{ ...input, cursor: "pointer" }}>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </Field>
          <Field label="Status">
            <div style={{ position: "relative" }}>
              <select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus)} disabled={!editable}
                style={{ ...input, cursor: editable ? "pointer" : "not-allowed" }}>
                {PROJECT_STATUSES.map((v) => <option key={v} value={v}>{PROJECT_STATUS_LABEL[v]}</option>)}
              </select>
            </div>
            {!editable && (
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>
                Follows Todoist: {status === "archived" ? "unarchive" : "archive"} it there to change this.
              </div>
            )}
          </Field>
        </Row>
        <Row>
          <Field label="Folder">
            <div style={{ position: "relative" }}>
              <select value={folderId} onChange={(e) => { setFolderId(e.target.value); if (e.target.value) setParent(""); }} style={{ ...input, cursor: "pointer" }}>
                <option value="">— Unfiled</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          </Field>
        </Row>
        <Field label="Part of (parent project)">
          <div style={{ position: "relative" }}>
            <select value={parent} onChange={(e) => { setParent(e.target.value); if (e.target.value) setFolderId(""); }} style={{ ...input, cursor: "pointer" }}>
              <option value="">— None (top level)</option>
              {parentOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>A project can sit in a folder or under a parent project — not both.</div>
        </Field>

        <Field label="Billing">
          <div style={{ position: "relative" }}>
            <select value={billingType} onChange={(e) => setBillingType(e.target.value as BillingType)} style={{ ...input, cursor: "pointer" }}>
              {BILLING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>
            {BILLING_HINT[billingType]}{billingType !== "internal" && " Internal time (admin, proposals) is always available too."}
          </div>
        </Field>

        {(billingType === "hourly" || billingType === "none") && (() => {
          const c = clients.find((x) => x.name.trim().toLowerCase() === client.trim().toLowerCase());
          const cur = project?.currency || c?.currency || "USD";
          return (
            <Field label="Hourly rate for this project">
              <input type="number" min={0} step={5} value={rate} onChange={(e) => setRate(e.target.value)} style={input}
                placeholder={c?.defaultRate ? String(c.defaultRate) : "e.g. 185"} />
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>
                {c?.defaultRate
                  ? `Leave blank to use ${c.name}'s rate (${money(c.defaultRate, cur)}/h). `
                  : c ? `${c.name} has no rate set, so set one here or on the client. ` : "Set a rate here, or link a client that has one. "}
                A change applies to new time only; logged time keeps the rate it was saved with.
              </div>
            </Field>
          );
        })()}

        {billingType === "fixed" && (
          <Row>
            <Field label="Budgeted hours">
              <input type="number" min={0} step={5} value={budgetHours} onChange={(e) => setBudgetHours(e.target.value)} style={input} placeholder="120" />
            </Field>
            <Field label="Fee amount (number)">
              <input type="number" min={0} step={500} value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)} style={input} placeholder="62000" />
            </Field>
          </Row>
        )}

        {billingType === "retainer" && (
          <Row>
            <Field label="Hours per month">
              <input type="number" min={0} step={1} value={retainerHours} onChange={(e) => setRetainerHours(e.target.value)} style={input} placeholder="20" />
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>
                Compared against the current month only, so the burn-down starts fresh each month.
              </div>
            </Field>
            <Field label="Monthly fee (number)">
              <input type="number" min={0} step={100} value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)} style={input} placeholder="10000" />
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 5 }}>
                Optional. Shows what the hours really earn: fee ÷ hours worked.
              </div>
            </Field>
          </Row>
        )}

        <Field label="Contract">
          <input value={contract} onChange={(e) => setContract(e.target.value)} style={input} placeholder="MSA / SOW reference or link" />
        </Field>
        <Field label="Scope">
          <textarea value={scope} onChange={(e) => setScope(e.target.value)} style={{ ...input, minHeight: 66, resize: "vertical", background: "var(--surface2)" }} placeholder="What the engagement covers" />
        </Field>

        <Field label="Colour">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {PROJECT_PALETTE.map((c) => (
              <button key={c} onClick={() => setColor(c)} aria-label={`Colour ${c}`} style={{
                width: 26, height: 26, borderRadius: 7, background: c, cursor: "pointer",
                border: color === c ? "2px solid var(--ink)" : "2px solid transparent",
                boxShadow: color === c ? "0 0 0 2px var(--surface)" : "none",
              }} />
            ))}
          </div>
        </Field>

        <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 20 }}>
          <span style={{ marginRight: "auto" }} />
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={submit} disabled={save.isPending} style={primaryBtn}>{isNew ? "Create project" : "Save changes"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 5 }}>{label}</div>
      {children}
    </label>
  );
}
/** A plain amount typed into the old free-text fee ("$45,000"), or "". */
function feeFromText(text?: string): string {
  const m = text?.trim().match(/^\$?\s*([\d,]+(?:\.\d+)?)$/);
  const n = m ? Number(m[1].replace(/,/g, "")) : 0;
  return n > 0 ? String(n) : "";
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}

const input: CSSProperties = { width: "100%", padding: "9px 11px", borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 13.5 };
const primaryBtn: CSSProperties = { padding: "9px 16px", borderRadius: 999, border: "none", background: "var(--accent)", color: "var(--onAccent)", fontSize: 13, fontWeight: 500, cursor: "pointer" };
const ghostBtn: CSSProperties = { padding: "9px 14px", borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)", fontSize: 13, cursor: "pointer" };
const iconBtn: CSSProperties = { width: 28, height: 28, borderRadius: 5, border: "none", background: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 14 };
